import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const TransferPage = () => {
  const [transfers, setTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [branches] = useState(["Lehulu", "Kalkidan", "Store"]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    fromBranch: "",
    toBranch: "",
    search: "",
    specificDate: "",
    startDate: "",
    endDate: "",
    monthYear: "",
  });
  const [formData, setFormData] = useState({
    item: null,
    quantity: "",
    unitprice: "",
    fromBranch: "",
    toBranch: "",
    reserveForBranch: "",
    note: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [itemFilters, setItemFilters] = useState({
    branch: "",
    category: "",
    search: "",
  });
  const [isLoading, setIsLoading] = useState({
    submit: false,
    cancel: false,
    selectItem: false,
    closeModal: false,
    openModal: false,
  });
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [viewingTransfer, setViewingTransfer] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    fetchTransfers();
    fetchItems();
    fetchCategories();
  }, [
    filters.fromBranch,
    filters.toBranch,
    filters.search,
    filters.specificDate,
    filters.startDate,
    filters.endDate,
    filters.monthYear,
    itemFilters.branch,
    itemFilters.category,
    itemFilters.search,
  ]);

  const fetchTransfers = async () => {
    try {
      let url = "https://backend.lehuluet.com/api/stockouts/stockouts";
      const queryParams = [];

      if (filters.fromBranch) queryParams.push(`fromBranch=${filters.fromBranch}`);
      if (filters.toBranch) {
        queryParams.push(`transfer=Transfer to ${filters.toBranch}`);
      }
      if (filters.specificDate) queryParams.push(`specificDate=${filters.specificDate}`);
      else if (filters.startDate && filters.endDate) {
        queryParams.push(`startDate=${filters.startDate}`);
        queryParams.push(`endDate=${filters.endDate}`);
      } else if (filters.monthYear) queryParams.push(`monthYear=${filters.monthYear}`);

      if (queryParams.length > 0) url += `?${queryParams.join("&")}`;

      const response = await axios.get(url);
      const transferData = response.data.data.filter(
        (stockout) => stockout.transfer && stockout.transfer !== "not transferd"
      );
      setTransfers(transferData);
    } catch (error) {
      toast.error("Error fetching transfers");
    }
  };

  const fetchItems = async () => {
    try {
      const url = itemFilters.branch
        ? `https://backend.lehuluet.com/api/items/items/branch/${itemFilters.branch}`
        : "https://backend.lehuluet.com/api/items/items";
      const response = await axios.get(url);
      let filteredData = response.data.data;

      if (itemFilters.category) {
        filteredData = filteredData.filter((item) => {
          const categoryId = item.category?._id
            ? item.category._id
            : item.category;
          return categoryId === itemFilters.category;
        });
      }
      if (itemFilters.search) {
        const searchLower = itemFilters.search.toLowerCase();
        filteredData = filteredData.filter(
          (item) =>
            item.name.toLowerCase().includes(searchLower) ||
            item.brand.toLowerCase().includes(searchLower)
        );
      }

      setItems(filteredData);
    } catch (error) {
      toast.error("Error fetching items");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        "https://backend.lehuluet.com/api/categories/categories"
      );
      setCategories(response.data.data);
    } catch (error) {
      toast.error("Error fetching categories");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "specificDate" || name === "monthYear") {
      setFilters({
        ...filters,
        [name]: value,
        startDate: "",
        endDate: "",
        ...(name === "specificDate" && { monthYear: "" }),
        ...(name === "monthYear" && { specificDate: "" }),
      });
    } else {
      setFilters({ ...filters, [name]: value });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "toBranch" && value !== "Store" ? { reserveForBranch: "" } : {})
    }));
  };

  const handleItemFilterChange = (e) => {
    setItemFilters({ ...itemFilters, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.item ||
      !formData.quantity ||
      !formData.fromBranch ||
      !formData.toBranch
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (formData.toBranch === "Store" && !formData.reserveForBranch) {
      toast.error("Please select a reserve branch when transferring to Store");
      return;
    }

    const quantity = Number(formData.quantity);
    if (isNaN(quantity)) {
      toast.error("Quantity must be a valid number");
      return;
    }

    if (quantity <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }

    if (formData.fromBranch === formData.toBranch) {
      toast.error("Source and destination branches cannot be the same");
      return;
    }

    setIsLoading((prev) => ({ ...prev, submit: true }));

    try {
      const submitData = {
        itemId: formData.item._id,
        quantity: quantity,
        fromBranch: formData.fromBranch,
        toBranch: formData.toBranch,
        unitprice: formData.unitprice ? Number(formData.unitprice) : 0,
        note: formData.note || "",
        reserveForBranch: formData.toBranch === "Store" ? formData.reserveForBranch : undefined
      };

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Your session has expired. Please login again.");
        return;
      }

      const response = await axios.post(
        "https://backend.lehuluet.com/api/items/items/createTransfer",
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response.data.success) {
        toast.success("Transfer created successfully");
        fetchTransfers();
        resetForm();
      } else {
        throw new Error(response.data.message || "Failed to create transfer");
      }
    } catch (error) {
      console.error("Transfer error:", error);
      if (error.response) {
        if (error.response.status === 401) {
          toast.error("Session expired. Please login again.");
          localStorage.removeItem("token");
        } else if (error.response.status === 400) {
          toast.error(error.response.data.message || "Invalid request");
        } else if (error.response.status === 500) {
          toast.error("Server error. Please try again later.");
        }
      } else if (error.request) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(error.message || "Error creating transfer");
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  const resetForm = () => {
    setIsLoading((prev) => ({ ...prev, cancel: true }));
    setFormData({
      item: null,
      quantity: "",
      unitprice: "",
      fromBranch: "",
      toBranch: "",
      reserveForBranch: "",
      note: "",
    });
    setEditingId(null);
    setIsLoading((prev) => ({ ...prev, cancel: false }));
  };

  const filteredTransfers = transfers.filter((transfer) => {
    const transferDate = new Date(
      transfer.transfer_date || transfer.created_at
    );
    const matchesSearch = filters.search
      ? transfer.item?.name
          ?.toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        transfer.item?.brand
          ?.toLowerCase()
          .includes(filters.search.toLowerCase())
      : true;

    let matchesDate = true;
    if (filters.specificDate) {
      const specificDate = new Date(filters.specificDate);
      matchesDate = transferDate.toDateString() === specificDate.toDateString();
    } else if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      matchesDate = transferDate >= start && transferDate <= end;
    } else if (filters.monthYear) {
      const [year, month] = filters.monthYear.split("-");
      matchesDate =
        transferDate.getFullYear() === parseInt(year) &&
        transferDate.getMonth() === parseInt(month) - 1;
    }

    return matchesSearch && matchesDate;
  });

  const selectItem = (item) => {
    setIsLoading((prev) => ({ ...prev, selectItem: true }));
    setFormData({ ...formData, item, fromBranch: item.branch });
    setIsItemModalOpen(false);
    setItemFilters({ branch: "", category: "", search: "" });
    setIsLoading((prev) => ({ ...prev, selectItem: false }));
  };

  const openModal = () => {
    setIsLoading((prev) => ({ ...prev, openModal: true }));
    setIsItemModalOpen(true);
    setIsLoading((prev) => ({ ...prev, openModal: false }));
  };

  const closeModal = () => {
    setIsLoading((prev) => ({ ...prev, closeModal: true }));
    setIsItemModalOpen(false);
    setIsLoading((prev) => ({ ...prev, closeModal: false }));
  };

  const openViewModal = (transfer) => {
    setViewingTransfer(transfer);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewingTransfer(null);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl text-teal-700 font-bold">Transfer Items</h1>
        <button
          onClick={() => setIsFilterVisible(!isFilterVisible)}
          className="px-3 py-1 bg-teal-500 text-white rounded-md text-sm hover:bg-teal-600 transition-colors"
        >
          {isFilterVisible ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {isFilterVisible && (
        <div className="bg-gray-100 p-4 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-1">From Branch</label>
              <select
                name="fromBranch"
                value={filters.fromBranch}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">All Branches</option>
                {branches.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-1">To Branch</label>
              <select
                name="toBranch"
                value={filters.toBranch}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">All Branches</option>
                {branches.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-1">Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by name or brand..."
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-1">Specific Date</label>
              <input
                type="date"
                name="specificDate"
                value={filters.specificDate}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-1">Month/Year</label>
              <input
                type="month"
                name="monthYear"
                value={filters.monthYear}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-gray-100 p-4 rounded mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <button
              type="button"
              onClick={openModal}
              disabled={isLoading.openModal}
              className="w-full p-3 border rounded-lg bg-white text-gray-400 flex items-center justify-center gap-2 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {isLoading.openModal ? (
                "Loading..."
              ) : formData.item ? (
                `${formData.item.name} - ${formData.item.brand}`
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                  Select Item
                </>
              )}
            </button>
          </div>
          <div className="hidden">
            <label className="block text-gray-700 mb-1">From Branch</label>
            <select
              name="fromBranch"
              value={formData.fromBranch}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select Source</option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 mb-1">To Branch</label>
            <select
              name="toBranch"
              value={formData.toBranch}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select Destination</option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>
          {formData.toBranch === "Store" && (
            <div>
              <label className="block text-gray-700 mb-1">Reserve For Branch</label>
              <select
                name="reserveForBranch"
                value={formData.reserveForBranch}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Select Reserve Branch</option>
                {branches.filter(branch => branch !== "Store").map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              placeholder="Quantity"
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Unit Price</label>
            <input
              type="number"
              name="unitprice"
              value={formData.unitprice}
              onChange={handleInputChange}
              placeholder="Unit Price"
              className="w-full p-2 border rounded"
              
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-gray-700 mb-1">Note</label>
          <input
            type="text"
            name="note"
            value={formData.note}
            onChange={handleInputChange}
            placeholder="Note (optional)"
            className="w-full p-2 border rounded"
          />
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={isLoading.submit}
            className="bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600 disabled:opacity-50"
          >
            {isLoading.submit ? "Loading..." : "Transfer Item"}
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={isLoading.cancel}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:opacity-50"
          >
            {isLoading.cancel ? "Loading..." : "Cancel"}
          </button>
        </div>
      </form>

      {isItemModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">Select Item</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-1">Branch</label>
                <select
                  name="branch"
                  value={itemFilters.branch}
                  onChange={handleItemFilterChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch} value={branch}>
                      {branch}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 mb-1">Category</label>
                <select
                  name="category"
                  value={itemFilters.category}
                  onChange={handleItemFilterChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  value={itemFilters.search}
                  onChange={handleItemFilterChange}
                  placeholder="Search items..."
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="p-2 text-left">Category</th>
                    <th className="p-2 text-left">Brand</th>
                    <th className="p-2 text-left">Size</th>
                    <th className="p-2 text-left">Position/Origin</th>
                    <th className="p-2 text-left">UniqueSize/AMP</th>
                    <th className="p-2 text-left">Available</th>
                    <th className="p-2 text-left">Branch</th>
                    <th className="p-2 text-left">Reserve For</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item._id} className="border-b hover:bg-gray-50">
                      <td className="p-2">{item.category.name}</td>
                      <td className="p-2">{item.brand}</td>
                      <td className="p-2">{item.size}</td>
                      <td className="p-2">{item.position || item.origin}</td>
                      <td className="p-2">{item.uniquesize || item.ampere}</td>
                      <td className="p-2">{item.quantity}</td>
                      <td className="p-2">{item.branch}</td>
                      <td className="p-2">{item.reserve_for_branch || "N/A"}</td>
                      <td className="p-2 flex gap-2">
                        <button
                          onClick={() => selectItem(item)}
                          disabled={isLoading.selectItem}
                          className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 disabled:opacity-50"
                        >
                          {isLoading.selectItem ? "Loading..." : "Select"}
                        </button>
                        <button
                          onClick={() => openViewModal(item)}
                          className="bg-teal-500 text-white px-2 py-1 rounded hover:bg-teal-600"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={closeModal}
              disabled={isLoading.closeModal}
              className="mt-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:opacity-50"
            >
              {isLoading.closeModal ? "Loading..." : "Close"}
            </button>
          </div>
        </div>
      )}

      {isViewModalOpen && viewingTransfer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-3xl">
            <h2 className="text-xl font-bold mb-4">Transfer Details</h2>
            {viewingTransfer.quantity && viewingTransfer.transfer ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-semibold text-lg mb-2">Transfer Info</h3>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Quantity:</span>{" "}
                      {viewingTransfer.quantity}
                    </p>
                    <p>
                      <span className="font-medium">Unit Price:</span> $
                      {viewingTransfer.unitprice || 0}
                    </p>
                    <p>
                      <span className="font-medium">Total:</span> $
                      {(
                        viewingTransfer.quantity *
                        (viewingTransfer.unitprice || 0)
                      ).toFixed(2)}
                    </p>
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      {new Date(
                        viewingTransfer.transfer_date ||
                          viewingTransfer.created_at
                      ).toLocaleString()}
                    </p>
                    <p>
                      <span className="font-medium">From Branch:</span>{" "}
                      {viewingTransfer.fromBranch || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">To Branch:</span>{" "}
                      {viewingTransfer.transfer?.replace("Transfer to ", "") ||
                        "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Reserve For Branch:</span>{" "}
                      {viewingTransfer.item?.reserve_for_branch || "N/A"}
                    </p>
                    {viewingTransfer.note && (
                      <p>
                        <span className="font-medium">Note:</span>{" "}
                        {viewingTransfer.note}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Item Info</h3>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Name:</span>{" "}
                      {viewingTransfer.item?.name || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Brand:</span>{" "}
                      {viewingTransfer.item?.brand || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Size:</span>{" "}
                      {viewingTransfer.item?.size || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Unique Size / AMP:</span>{" "}
                      {viewingTransfer.item?.uniquesize ||
                        viewingTransfer.item?.ampere ||
                        "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Category:</span>{" "}
                      {viewingTransfer.item?.category?.name || "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">User Info</h3>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Name:</span>{" "}
                      {viewingTransfer.user?.name || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Email:</span>{" "}
                      {viewingTransfer.user?.email || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold">Basic Information</h3>
                  <div className="mt-2 space-y-2">
                    <p>
                      <span className="font-medium">Name:</span>{" "}
                      {viewingTransfer.name}
                    </p>
                    <p>
                      <span className="font-medium">Brand:</span>{" "}
                      {viewingTransfer.brand}
                    </p>
                    <p>
                      <span className="font-medium">Branch:</span>{" "}
                      {viewingTransfer.branch}
                    </p>
                    <p>
                      <span className="font-medium">Quantity:</span>{" "}
                      {viewingTransfer.quantity}
                    </p>
                    <p>
                      <span className="font-medium">Reserve For Branch:</span>{" "}
                      {viewingTransfer.reserve_for_branch || "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold">Additional Details</h3>
                  <div className="mt-2 space-y-2">
                    <p>
                      <span className="font-medium">Size:</span>{" "}
                      {viewingTransfer.size || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Unique Size:</span>{" "}
                      {viewingTransfer.uniquesize || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Ampere:</span>{" "}
                      {viewingTransfer.ampere || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Position:</span>{" "}
                      {viewingTransfer.position || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Origin:</span>{" "}
                      {viewingTransfer.origin || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Category:</span>{" "}
                      {viewingTransfer.category?.name || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={closeViewModal}
              className="mt-6 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Brand</th>
              <th className="p-2 text-left">From Branch</th>
              <th className="p-2 text-left">To Branch</th>
              <th className="p-2 text-left">Reserve For</th>
              <th className="p-2 text-left">Quantity</th>
              <th className="p-2 text-left">Unit Price</th>
              <th className="p-2 text-left">Total</th>
              <th className="p-2 text-left">Note</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransfers.map((transfer) => (
              <tr key={transfer._id} className="border-b hover:bg-gray-50">
                <td className="p-2">
                  {new Date(
                    transfer.transfer_date || transfer.created_at
                  ).toLocaleDateString()}
                </td>
                <td className="p-2">{transfer.item?.brand || "N/A"}</td>
                <td className="p-2">{transfer.fromBranch || "N/A"}</td>
                <td className="p-2">
                  {transfer.transfer?.replace("Transfer to ", "") || "N/A"}
                </td>
                <td className="p-2">{transfer.item?.reserve_for_branch || "N/A"}</td>
                <td className="p-2">{transfer.quantity || 0}</td>
                <td className="p-2">${transfer.unitprice || 0}</td>
                <td className="p-2">
                  ${(transfer.quantity * (transfer.unitprice || 0)).toFixed(2)}
                </td>
                <td className="p-2">{transfer.note || "----"}</td>
                <td className="p-2">
                  <button
                    onClick={() => openViewModal(transfer)}
                    className="bg-teal-500 text-white px-2 py-1 rounded hover:bg-teal-600"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default TransferPage;